import React, { useEffect, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Menu, MenuItem, Box, Typography, Divider } from '@mui/material'
import Avatar from '@/Components/Elements/AvatarWithPresence'
import SelectUserPresence from '@/Components/Elements/SelectUserPresence'
import LoadingDefault from '@/Components/Elements/Loading/LoadingDefault'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faUserCircle,
    faList,
    faUsers,
    faQuestion,
    faPowerOff,
    faUserSecret,
} from '@fortawesome/pro-duotone-svg-icons'

import { UserProfileProps } from '../model/types'
import { getUserMenuEntries } from '../model/userprofile.utils'
import { toast } from 'react-toastify'

type MeResponse = {
    id: number
    username: string
    roles: string[]
    is_impersonated?: boolean
    impersonator_identifier?: string | null
}

export const UserProfile: React.FC<UserProfileProps> = ({
    anchorEl,
    onClose,
    kedo,
    handleLogoutClick,
    loading = false,
}) => {
    const menuEntries = getUserMenuEntries(kedo, onClose, handleLogoutClick)

    const [meLoading, setMeLoading] = useState(false)
    const [isImpersonated, setIsImpersonated] = useState(false)
    const [impersonatorIdentifier, setImpersonatorIdentifier] = useState<
        string | null
    >(null)

    useEffect(() => {
        const isOpen = Boolean(anchorEl)

        if (!isOpen) {
            return
        }

        let cancelled = false

        const loadMe = async () => {
            setMeLoading(true)

            try {
                const response = await kedo
                    .api()
                    .get(kedo.api().getUserMeEndpoint())
                console.log(response)
                const me = response?.data as MeResponse

                if (cancelled) {
                    return
                }

                setIsImpersonated(me?.is_impersonated === true)
                setImpersonatorIdentifier(me?.impersonator_identifier ?? null)
            } catch (error) {
                if (!cancelled) {
                    setIsImpersonated(false)
                    setImpersonatorIdentifier(null)
                }
            } finally {
                if (!cancelled) {
                    setMeLoading(false)
                }
            }
        }

        void loadMe()

        return () => {
            cancelled = true
        }
    }, [anchorEl, kedo])

    const handleStopImpersonation = async () => {
        try {
            const response = await kedo
                .api()
                .post(kedo.api().getImpersonateStopEndpoint(), {})
            if (!response.data?.ok) {
                throw new Error(
                    response.data || 'Failed to return as original user'
                )
            }

            localStorage.setItem('roles', JSON.stringify(response.data.roles))
            localStorage.setItem('user_id', response.data.id)
            localStorage.setItem('username', response.data.username)

            toast.info(kedo.t(`Redirecting back to your original user`))
            onClose()
            setTimeout(() => window.location.reload(), 3000)
        } catch (error) {
            toast.error(kedo.t(`Failed to return as original user`))
        }
    }

    return (
        <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={onClose}
            PaperProps={{ sx: { minWidth: 220, minHeight: 300 } }}
        >
            {loading ? (
                <Box sx={{ textAlign: 'center', p: 2 }}>
                    <LoadingDefault />
                </Box>
            ) : (
                <>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            px: 1.5,
                            py: 1,
                            borderBottom: (theme) =>
                                `1px solid ${theme.palette.divider}`,
                        }}
                    >
                        <Avatar
                            kedo={kedo}
                            user={{
                                userId: kedo.user().getUserId(),
                                username: kedo.user().getUsername(),
                            }}
                            props={{
                                anchorEl,
                                onClose,
                                kedo,
                                handleLogoutClick,
                            }}
                            options={{
                                togglePresence:
                                    kedo.env()?.hasEnviroment() ?? false,
                                size: 36,
                                showPresence:
                                    kedo.env()?.hasEnviroment() ?? false,
                            }}
                        />
                        <Box sx={{ ml: 2 }}>
                            <Typography>
                                {kedo.user().getUsername() ?? '?'}
                            </Typography>

                            {meLoading ? null : isImpersonated ? (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: 'error.main',
                                        display: 'block',
                                    }}
                                >
                                    Impersonating
                                    {impersonatorIdentifier
                                        ? ` (from ${impersonatorIdentifier})`
                                        : ''}
                                </Typography>
                            ) : null}
                        </Box>
                    </Box>

                    {isImpersonated && (
                        <>
                            <Box
                                sx={{
                                    px: 1.5,
                                    py: 1,
                                    bgcolor: 'error.light',
                                    color: 'error.contrastText',
                                }}
                            >
                                <Typography variant="body2" fontWeight={600}>
                                    {kedo.t('You are impersonating this user')}
                                </Typography>
                                {impersonatorIdentifier && (
                                    <Typography variant="caption">
                                        {kedo.t(
                                            'Original user: {{username}} ',
                                            { username: impersonatorIdentifier }
                                        )}
                                    </Typography>
                                )}
                            </Box>

                            <MenuItem onClick={handleStopImpersonation}>
                                <Box
                                    sx={{
                                        width: 20,
                                        height: 20,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <FontAwesomeIcon
                                        icon={faUserSecret}
                                        style={{ fontSize: 14 }}
                                    />
                                </Box>
                                <Typography variant="body2" sx={{ ml: 2 }}>
                                    {kedo.t('Stop impersonation')}
                                </Typography>
                            </MenuItem>

                            <Divider />
                        </>
                    )}

                    <MenuItem
                        component={RouterLink}
                        to="/profile"
                        onClick={onClose}
                    >
                        <Box
                            sx={{
                                width: 20,
                                height: 20,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <FontAwesomeIcon
                                icon={faUserCircle}
                                style={{ fontSize: 16 }}
                            />
                        </Box>
                        <Typography variant="body2" sx={{ ml: 2 }}>
                            {kedo.t('My profile')}
                        </Typography>
                    </MenuItem>

                    {kedo.isAdmin() && (
                        <MenuItem
                            component={RouterLink}
                            to="/subscriptions"
                            onClick={onClose}
                        >
                            <Box
                                sx={{
                                    width: 20,
                                    height: 20,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={faList}
                                    style={{ fontSize: 14 }}
                                />
                            </Box>
                            <Typography variant="body2" sx={{ ml: 2 }}>
                                {kedo.t('Subscriptions')}
                            </Typography>
                        </MenuItem>
                    )}

                    <MenuItem
                        component={RouterLink}
                        to="/colleagues"
                        onClick={onClose}
                    >
                        <Box
                            sx={{
                                width: 20,
                                height: 20,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <FontAwesomeIcon
                                icon={faUsers}
                                style={{ fontSize: 14 }}
                            />
                        </Box>
                        <Typography variant="body2" sx={{ ml: 2 }}>
                            {kedo.t('Colleagues')}
                        </Typography>
                    </MenuItem>

                    <Divider />

                    <SelectUserPresence
                        kedo={kedo}
                        user={{
                            userId: kedo.user().getUserId(),
                            username: kedo.user().getUsername(),
                        }}
                        props={{ anchorEl, onClose, kedo, handleLogoutClick }}
                        options={{
                            togglePresence:
                                kedo.env()?.hasEnviroment() ?? false,
                            size: 36,
                            showPresence: kedo.env()?.hasEnviroment() ?? false,
                        }}
                    />

                    <Divider />

                    <MenuItem
                        component={RouterLink}
                        to="/contact"
                        onClick={onClose}
                    >
                        <Box
                            sx={{
                                width: 20,
                                height: 20,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <FontAwesomeIcon
                                icon={faQuestion}
                                style={{ fontSize: 14 }}
                            />
                        </Box>
                        <Typography variant="body2" sx={{ ml: 2 }}>
                            {kedo.t('Contact')}
                        </Typography>
                    </MenuItem>

                    <MenuItem onClick={handleLogoutClick}>
                        <Box
                            sx={{
                                width: 20,
                                height: 20,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <FontAwesomeIcon
                                icon={faPowerOff}
                                style={{ fontSize: 14 }}
                            />
                        </Box>
                        <Typography variant="body2" sx={{ ml: 2 }}>
                            {kedo.t('Logout')}
                        </Typography>
                    </MenuItem>
                </>
            )}
        </Menu>
    )
}
